/**
 * ChatFlow Registry (jurisdiction-agnostic)
 *
 * Plug-in 패턴 — registry 자체는 shared 에 위치하고 비어있는 상태로 시작.
 * 각 jurisdiction(my, sg) 의 chatflow 들은 자기 자신을 등록한다.
 *
 * 사용:
 *   // my 측: my/preset/chatflows/index.ts
 *   import { registerChatFlow } from '@/.../shared/preset/chatflows/chatFlowRegistry';
 *   import { incorporationFlow } from './incorporationFlow';
 *   registerChatFlow(incorporationFlow);
 *
 *   // app 부팅 (main.ts) 에서 한 번 import 해서 side-effect 등록 트리거.
 *   import '@/features/startTask/preset_task/my/preset/chatflows';
 */

import type { ChatFlow } from './types';
import { defaultFlow } from './defaultFlow';

const flows: ChatFlow[] = [];

/** 새 chatflow 등록 (jurisdiction 별 부팅 시 호출). */
export function registerChatFlow(flow: ChatFlow): void {
  if (!flow?.id) {
    console.warn('[chatFlowRegistry] flow.id 누락 — 등록 스킵', flow);
    return;
  }
  if (flows.some((f) => f.id === flow.id)) {
    console.warn(`[chatFlowRegistry] flow.id 중복 — 덮어쓰기: ${flow.id}`);
    const idx = flows.findIndex((f) => f.id === flow.id);
    flows[idx] = flow;
    return;
  }
  flows.push(flow);
}

/** 카테고리·TASK_CODE 로 ChatFlow 반환. 매칭 안 되면 defaultFlow. */
export function getChatFlow(categoryName: string, taskCode?: string | null): ChatFlow {
  const code = String(taskCode || '').trim().toUpperCase();
  const normalized = (categoryName || '').toLowerCase();

  if (code) {
    const byCode = flows.filter((f) =>
      (f.taskCodeMatch || []).some((c) => String(c).trim().toUpperCase() === code),
    );
    if (byCode.length === 1) {
      const only = byCode[0];
      // M32014: Incorporation DOC vs 레거시 AGM Unaudited — AGM 카테고리면 스킵
      if (
        only.id === 'incorporation-doc' &&
        (/annual\s*general|\bagm\b/.test(normalized))
      ) {
        // fall through to category match
      } else {
        return only;
      }
    } else if (byCode.length > 1) {
      const byCat = byCode.find((f) =>
        f.categoryMatch.some((m) => normalized.includes(m.toLowerCase())),
      );
      if (byCat) return byCat;
      return byCode[0];
    }
  }

  const flow = flows.find((f) =>
    f.categoryMatch.some((m) => normalized.includes(m.toLowerCase())),
  );
  return flow || defaultFlow;
}

/** flow id 로 ChatFlow 반환 (없으면 null). */
export function getChatFlowById(id: string): ChatFlow | null {
  const key = String(id || '').trim();
  if (!key) return null;
  return flows.find((f) => f.id === key) || null;
}

/** 등록된 모든 flow id 목록. */
export function getRegisteredFlowIds(): string[] {
  return flows.map((f) => f.id);
}

/** 테스트용 — registry 비우기. */
export function _clearChatFlowRegistry(): void {
  flows.length = 0;
}
